import { Entities } from '@getvim/vim-connect';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import { UserConfig } from '../../../../../hooks';
import getInsurerLogoURL from '../../../../../utils/getInsurerLogoURL';
import {
  OrderAssistOption,
  OrderAssistResultsPDFInput,
  Patient,
  PDFProviderDetails,
  PDFAcceptingNewPatientsType,
} from '../../../types';
import addressFormat from '../../../utils/addressFormatter';
import { capitalizeAsName, isProviderResult } from '../../../utils/general';
import { getSelectedItemAddress } from '../../../utils/search/getSelectedAddress';
import { isNull } from 'lodash-es';

interface FormatInputProps {
  supportScoreRatingBadges: boolean;
  supportNetworkStatusBadge: boolean;
  supportOpeningHours: boolean;
  useGenericSearchTitles: boolean;
  displayDistanceInExportDetails: boolean;
  productConfig: UserConfig;
  patient?: Entities.Patient;
  logoUrl?: string;
  searchResults?: OrderAssistOption[];
  featuredResults?: OrderAssistOption[];
  alternativeResults?: OrderAssistOption[];
  supportAcceptingNewPatientsInCard: boolean;
}

const getProviderName = (option: OrderAssistOption): string => {
  if (isProviderResult(option)) {
    return capitalizeAsName(
      `${option.details.complexName.firstName} ${option.details.complexName.lastName}`,
    );
  }
  if (option.details.acoName) {
    return capitalizeAsName(option.details.acoName);
  }
  return capitalizeAsName(option.details.name);
};

const getProviderDetailsPDFInput = ({
  item,
  supportScoreRatingBadges,
  supportNetworkStatusBadge,
  supportOpeningHours,
  displayDistanceInExportDetails,
  supportAcceptingNewPatientsInCard,
}: {
  item: OrderAssistOption;
  supportScoreRatingBadges: boolean;
  supportNetworkStatusBadge: boolean;
  supportOpeningHours: boolean;
  displayDistanceInExportDetails: boolean;
  supportAcceptingNewPatientsInCard: boolean;
}): PDFProviderDetails => {
  const selectedAddress = getSelectedItemAddress(item);
  const fullAddress = addressFormat(selectedAddress);
  const providerName = getProviderName(item);
  const specialties = item.details.specialties.map((x) => capitalizeAsName(x.description));
  const pdfDetails: PDFProviderDetails = {
    providerName,
    specialties,
    officeName: selectedAddress.officeName || undefined,
    address: fullAddress || undefined,
    phoneNumber: selectedAddress.phone,
    faxNumber: selectedAddress.fax,
    npi: item.details.npi,
  };

  if (
    displayDistanceInExportDetails &&
    selectedAddress?.distance &&
    Number.isFinite(Number(selectedAddress?.distance))
  ) {
    pdfDetails.distance = Number.parseFloat(selectedAddress.distance).toFixed(2) + ' mi';
  }

  if (supportAcceptingNewPatientsInCard && !isNull(selectedAddress?.acceptNewPatients)) {
    pdfDetails.acceptingNewPatients = selectedAddress.acceptNewPatients
      ? PDFAcceptingNewPatientsType.Yes
      : PDFAcceptingNewPatientsType.No;
  }

  if (supportScoreRatingBadges) {
    pdfDetails.score = item.teladocGarnerExtraInfo?.score?.toString();
    pdfDetails.rating = item.teladocGarnerExtraInfo?.rating?.toString();
  }

  if (supportOpeningHours && selectedAddress?.openingHours?.length) {
    pdfDetails.openingHours = selectedAddress.openingHours.map(
      (openingHour) => `${openingHour.daysRange}: ${openingHour.open} - ${openingHour.close}`,
    );
  }

  if (supportNetworkStatusBadge) {
    pdfDetails.networkStatus = item.teladocGarnerExtraInfo?.networkStatus
      ? 'In network'
      : 'Out of network';
  }
  return pdfDetails;
};

export const resultsPDFFormatters = {
  formatFilename: (patient?: Patient): string => {
    const date = dayjs().format('MM-DD-YY');
    const prefix = patient?.demographics.firstName ? `${patient?.demographics.firstName} - ` : '';
    return `${prefix}Order Assist Results - ${date}.pdf`;
  },
  formatInput: ({
    supportScoreRatingBadges,
    supportNetworkStatusBadge,
    supportOpeningHours,
    useGenericSearchTitles,
    displayDistanceInExportDetails,
    productConfig,
    patient,
    logoUrl: logoUrlFromDatasource,
    searchResults,
    featuredResults,
    alternativeResults,
    supportAcceptingNewPatientsInCard,
  }: FormatInputProps): OrderAssistResultsPDFInput => {
    if (!productConfig.vimConnectUser) {
      throw new Error('cannot generated pdf input because vimConnectUser is undefined');
    }
    const { organization, vimConnectUser } = productConfig;
    const { firstName, lastName } = vimConnectUser;

    const logoURL =
      logoUrlFromDatasource ||
      (patient?.insurance?.insurer ? getInsurerLogoURL(patient?.insurance?.insurer) : undefined);

    const input: OrderAssistResultsPDFInput = {
      patient: patient
        ? {
            name: `${patient.demographics.firstName} ${patient.demographics.lastName}`,
            dateOfBirth: dayjs(patient.demographics.dateOfBirth).format('MMM D, YYYY'),
            age: dayjs().diff(patient.demographics.dateOfBirth, 'y'),
          }
        : undefined,
      clinicName: organization.name,
      exportingUser: `${firstName} ${lastName}`,
      dateOfExporting: dayjs().format('MMM D YYYY'),
      results: searchResults?.map((item) =>
        getProviderDetailsPDFInput({
          item,
          supportScoreRatingBadges,
          supportNetworkStatusBadge,
          supportOpeningHours,
          displayDistanceInExportDetails,
          supportAcceptingNewPatientsInCard,
        }),
      ),
      featuredResults: featuredResults?.map((item) =>
        getProviderDetailsPDFInput({
          item,
          supportScoreRatingBadges,
          supportNetworkStatusBadge,
          supportOpeningHours,
          displayDistanceInExportDetails,
          supportAcceptingNewPatientsInCard,
        }),
      ),
      alternativeResults: alternativeResults?.map((item) =>
        getProviderDetailsPDFInput({
          item,
          supportScoreRatingBadges,
          supportNetworkStatusBadge,
          supportOpeningHours,
          displayDistanceInExportDetails,
          supportAcceptingNewPatientsInCard,
        }),
      ),
      datasourceLogo: logoURL ? Buffer.from(logoURL).toString('base64') : undefined,
      useGenericSearchTitles,
    };
    return input;
  },
};
